// Project picker for the create wizard's "Tên toà nhà / dự án" field.
//
// A creatable combobox with three ways to fill the field:
//   1. Pick an existing project → links the unit + prefills the shared fields
//      (ward, address, total floors) and auto-numbers the code.
//   2. "＋ Tạo dự án …" → opens the project form dialog, then auto-selects the
//      new project.
//   3. "Dùng … làm tên toà nhà" → a free, unlinked building name (the old
//      behaviour, for one-off units that aren't part of a managed project).

import { useMemo, useState } from "react";
import { Building2, ChevronDown, Plus, Type } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useProjects, type Project } from "@/features/project/api";
import { ProjectFormDialog } from "@/features/project/components/project-form-dialog";
import type { PropertyDraft } from "@/features/property/wizard/wizard-state";

export function ProjectPickerField({
  draft,
  onChange,
  invalid,
}: {
  draft: PropertyDraft;
  onChange: (patch: Partial<PropertyDraft>) => void;
  invalid?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const projectsQuery = useProjects({}, true);
  const projects = useMemo(() => projectsQuery.data ?? [], [projectsQuery.data]);

  const selected = useMemo(
    () =>
      draft.projectId != null
        ? projects.find((p) => p.id === draft.projectId) ?? null
        : null,
    [draft.projectId, projects],
  );

  const term = search.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      term === ""
        ? projects
        : projects.filter((p) => p.name.toLowerCase().includes(term)),
    [projects, term],
  );

  const selectProject = (p: Project) => {
    onChange({
      projectId: p.id,
      buildingName: p.name,
      // Only override a shared field when the project actually defines it, so
      // a project with no ward doesn't wipe a ward the user already picked
      // (mirrors the backend's COALESCE-on-link behaviour).
      ...(p.wardCode != null ? { wardCode: p.wardCode } : {}),
      ...(p.streetAddress != null ? { streetAddress: p.streetAddress } : {}),
      ...(p.totalFloors != null ? { totalFloors: p.totalFloors } : {}),
      // The project auto-numbers unit codes from its prefix — drop any typed
      // code so the backend assigns the next one.
      ...(p.codePrefix ? { internalCode: undefined } : {}),
    });
    setOpen(false);
    setSearch("");
  };

  const applyFreeText = (text: string) => {
    onChange({ projectId: undefined, buildingName: text });
    setOpen(false);
    setSearch("");
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={projectsQuery.isPending}
            aria-label="Chọn hoặc tạo dự án"
            className={cn(
              "flex w-full items-center gap-2.5 rounded-[9px] border bg-background px-2.5 py-2 text-left transition-colors",
              "hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring",
              "disabled:cursor-not-allowed disabled:opacity-60",
              invalid ? "border-destructive" : "border-border-strong",
            )}
          >
            <span
              className={cn(
                "flex size-9 shrink-0 items-center justify-center rounded-[7px]",
                selected !== null
                  ? "bg-primary/10 text-primary"
                  : "bg-muted text-muted-foreground",
              )}
            >
              <Building2 aria-hidden="true" className="size-4" />
            </span>
            <div className="flex min-w-0 flex-1 flex-col">
              {selected !== null ? (
                <>
                  <span className="truncate text-[14px] font-semibold leading-tight">
                    {selected.name}
                  </span>
                  <span className="truncate text-[11px] text-primary">
                    Thuộc dự án · mã căn tự đánh số
                  </span>
                </>
              ) : draft.buildingName ? (
                <>
                  <span className="truncate text-[14px] font-semibold leading-tight">
                    {draft.buildingName}
                  </span>
                  <span className="truncate text-[11px] text-muted-foreground">
                    Không thuộc dự án
                  </span>
                </>
              ) : (
                <span className="text-[14px] font-medium text-muted-foreground">
                  {projectsQuery.isPending
                    ? "Đang tải dự án…"
                    : "Chọn hoặc tạo dự án"}
                </span>
              )}
            </div>
            <ChevronDown
              aria-hidden="true"
              className="size-3.5 shrink-0 text-muted-foreground"
            />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[var(--radix-popover-trigger-width)] min-w-[320px] p-0"
        >
          <Command shouldFilter={false}>
            <CommandInput
              value={search}
              onValueChange={setSearch}
              placeholder="Tìm hoặc tạo dự án…"
            />
            <CommandList>
              {filtered.length > 0 ? (
                <CommandGroup heading="Dự án">
                  {filtered.map((p) => (
                    <CommandItem
                      key={p.id}
                      value={String(p.id)}
                      onSelect={() => selectProject(p)}
                    >
                      <Building2 aria-hidden="true" />
                      <span className="flex-1 truncate">{p.name}</span>
                      <span className="font-mono text-[11px] text-muted-foreground">
                        {p.unitCount} căn
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ) : null}

              {term !== "" ? (
                <>
                  {filtered.length > 0 ? <CommandSeparator /> : null}
                  <CommandGroup>
                    <CommandItem
                      value="__create__"
                      onSelect={() => {
                        setOpen(false);
                        setCreateOpen(true);
                      }}
                    >
                      <Plus aria-hidden="true" />
                      Tạo dự án “{search.trim()}”
                    </CommandItem>
                    <CommandItem
                      value="__free__"
                      onSelect={() => applyFreeText(search.trim())}
                    >
                      <Type aria-hidden="true" />
                      Dùng “{search.trim()}” làm tên toà nhà
                    </CommandItem>
                  </CommandGroup>
                </>
              ) : filtered.length === 0 ? (
                <div className="py-6 text-center text-[14px] text-muted-foreground">
                  Nhập để tìm hoặc tạo dự án.
                </div>
              ) : null}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <ProjectFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        initialName={search.trim()}
        onSaved={(saved) => selectProject(saved)}
      />
    </>
  );
}
